import React, { useState, useEffect, useRef } from 'react';
import './MediaGallery.css'; // Import the CSS file
import ModelViewer from '../../components/ModelViewer/ModelViewer';

const MediaGallery = ({ mediaItems = [], autoAdvanceInterval = 30000, isDevMode = false }) => {
  // Set the first item as selected by default
  const [selectedMedia, setSelectedMedia] = useState(mediaItems[0] || null);
  // Reference to the video element for controlling playback
  const videoRef = useRef(null);
  // Reference to the model viewer for 3D models
  const modelViewerRef = useRef(null);
  // Reference for auto-advance timer
  const autoAdvanceTimerRef = useRef(null);
  // State to track model loading errors
  const [modelError, setModelError] = useState(null);
  // Add a new state for the progress timer
  const [progress, setProgress] = useState(0);
  // Add a new state for the remaining time
  const [remainingTime, setRemainingTime] = useState(autoAdvanceInterval / 1000);
  // Add state for video duration
  const [videoDuration, setVideoDuration] = useState(0);
  // Add state for video playing status
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Update selected media when the mediaItems prop changes
  useEffect(() => {
    if (mediaItems.length > 0 && !selectedMedia) {
      setSelectedMedia(mediaItems[0]);
    } else if (mediaItems.length > 0 && !mediaItems.some(item => item.id === selectedMedia?.id)) {
      // If the current selection is no longer in the list, reset to first item
      setSelectedMedia(mediaItems[0]);
    }
    
    // Reset model error when changing media items
    setModelError(null);
  }, [mediaItems, selectedMedia]);

  // Function to advance to the next media item
  const advanceToNextItem = () => {
    if (mediaItems.length <= 1) return;
    
    const currentIndex = mediaItems.findIndex(item => item.id === selectedMedia?.id);
    const nextIndex = (currentIndex + 1) % mediaItems.length;
    setSelectedMedia(mediaItems[nextIndex]);
    
    // Reset model error when changing media
    setModelError(null);
  };

  // Handle video ended event
  const handleVideoEnded = () => {
    // Clear the auto-advance timer since we're manually advancing
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }
    advanceToNextItem();
  };

  // Handle video metadata loaded
  const handleVideoMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration * 1000); // Convert to milliseconds
    }
  };

  // Handle video play/pause
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    // Reset progress when starting playback
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      setProgress((currentTime / duration) * 100);
      setRemainingTime(duration - currentTime);
    }
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  // Handle video time update
  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      const newProgress = (currentTime / duration) * 100;
      const newRemainingTime = duration - currentTime;
      
      setProgress(Math.min(newProgress, 100));
      setRemainingTime(Math.max(newRemainingTime, 0));
    }
  };

  // Add a more frequent update interval for video progress
  useEffect(() => {
    if (selectedMedia?.type === 'video' && videoRef.current) {
      const updateInterval = setInterval(() => {
        if (videoRef.current) {
          const currentTime = videoRef.current.currentTime;
          const duration = videoRef.current.duration;
          const newProgress = (currentTime / duration) * 100;
          const newRemainingTime = duration - currentTime;
          
          setProgress(Math.min(newProgress, 100));
          setRemainingTime(Math.max(newRemainingTime, 0));
        }
      }, 16); // Update approximately 60 times per second

      return () => clearInterval(updateInterval);
    }
  }, [selectedMedia]);

  // Update the auto-advance timer effect to handle videos differently
  useEffect(() => {
    // Clear any existing timer
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }
    
    // Skip auto-advance for 3D models to allow interaction
    if (selectedMedia?.type === 'model') {
      return;
    }
    
    // Reset progress and remaining time when media changes
    setProgress(0);
    setIsVideoPlaying(false);
    
    const startTime = Date.now();
    
    // For videos, use the video duration instead of fixed interval
    const interval = selectedMedia?.type === 'video' ? videoDuration : autoAdvanceInterval;
    setRemainingTime(interval / 1000);
    
    // Only set the timer if it's not a video
    if (selectedMedia?.type !== 'video') {
      // Set new timer
      autoAdvanceTimerRef.current = setTimeout(() => {
        advanceToNextItem();
      }, interval);
      
      // Update progress and remaining time every 100ms for non-video media
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const newProgress = (elapsed / interval) * 100;
        const newRemainingTime = (interval - elapsed) / 1000;
        
        setProgress(Math.min(newProgress, 100));
        setRemainingTime(Math.max(newRemainingTime, 0));
      }, 100);
      
      // Cleanup function
      return () => {
        if (autoAdvanceTimerRef.current) {
          clearTimeout(autoAdvanceTimerRef.current);
        }
        clearInterval(progressInterval);
      };
    }
  }, [selectedMedia, autoAdvanceInterval, videoDuration, mediaItems.length]);

  // Handle media selection
  const handleMediaSelect = (media) => {
    setSelectedMedia(media);
    // Reset model error when manually selecting media
    setModelError(null);
    
    // Reset auto-advance timer when manually selecting media
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }
  };

  // Effect to handle video autoplay when selected media changes
  useEffect(() => {
    if (selectedMedia?.type === 'video' && videoRef.current) {
      // Use a small timeout to ensure the video element is fully loaded
      const playPromise = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('Autoplay prevented:', err);
            // Many browsers prevent autoplay unless video is muted
            if (videoRef.current) {
              videoRef.current.muted = true;
              videoRef.current.play().catch(err => {
                console.log('Autoplay still prevented:', err);
              });
            }
          });
        }
      }, 100);
      
      return () => clearTimeout(playPromise);
    }
  }, [selectedMedia]);

  // Handle model errors
  const handleModelError = (error) => {
    console.error("Model error:", error);
    setModelError(error.message || "Failed to load 3D model");
  };

  // If no media items are provided, show a placeholder
  if (mediaItems.length === 0) {
    return (
      <div className="gallery-container">
        <div className="empty-placeholder">
          <p className="empty-message">No media items to display</p>
        </div>
      </div>
    );
  }

  // Function to render the main media content based on type
  const renderMainMedia = () => {
    if (!selectedMedia) return null;

    if (selectedMedia.type === 'image') {
      return (
        <img 
          src={selectedMedia.src} 
          alt={selectedMedia.alt || `Image ${selectedMedia.id}`}
          className="media-item"
        />
      );
    } else if (selectedMedia.type === 'video') {
      return (
        <video 
          muted
          ref={videoRef}
          src={selectedMedia.src} 
          poster={selectedMedia.poster || selectedMedia.thumbnail}
          controls
          className="media-item"
          onEnded={handleVideoEnded}
          onLoadedMetadata={handleVideoMetadata}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onTimeUpdate={handleVideoTimeUpdate}
        />
      );
    } else if (selectedMedia.type === 'model') {
      return (
        <div className="model-container media-item">
          {modelError ? (
            <div className="model-error-container">
              <div className="model-error-message">
                Error: {modelError}
              </div>
              <button 
                className="retry-button"
                onClick={() => {
                  setModelError(null); // Reset error to try again
                  // Force re-render by setting a key timestamp
                  setSelectedMedia({...selectedMedia, _timestamp: Date.now()});
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <ModelViewer
              modelPath={selectedMedia.src}
              cameraPosition={selectedMedia.cameraPosition}
              cameraFov={selectedMedia.cameraFov}
              ref={modelViewerRef}
              isDevMode={isDevMode}
            />
          )}
        </div>
      );
    }
    return null;
  };

  // Function to render the thumbnail based on media type
  const renderThumbnail = (media) => {
    return (
      <div className="thumbnail-wrapper">
        <img 
          src={media.thumbnail || media.src} 
          alt={media.alt || `Thumbnail ${media.id}`}
          className="thumbnail-image"
        />
        {media.type === 'video' && (
          <div className="video-overlay">
            <div className="play-button">
              <div className="play-icon"></div>
            </div>
          </div>
        )}
        {media.type === 'model' && (
          <div className="model-overlay">
            <div className="model-icon">3D</div>
          </div>
        )}
      </div>
    );
  };

  const handleThumbnailClick = (index) => {
    setSelectedMedia(mediaItems[index]);
    setProgress(0);
    setRemainingTime(autoAdvanceInterval / 1000);
  };

  return (
    <div className="gallery-container">
      {/* Main media display */}
      <div className="media-display">
        <div className="media-container">
          <div className='media-title-container z-5'>
            <div className='media-title'>{selectedMedia.title}</div>
            <div className='media-title2'>{selectedMedia.title2}</div>
          </div>
          <div className='media-title-container z-negative-5'>
            <div className='media-title'>{selectedMedia.title}</div>
            <div className='media-title2'>{selectedMedia.title2}</div>
          </div>
          
          {renderMainMedia()}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="timer-container">
        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
        </div>
      </div>
      
      {/* Thumbnail row */}
      <div className="thumbnails-row">
        {mediaItems.map((media, index) => (
          <div 
            key={media.id}
            className={`thumbnail-container ${
              selectedMedia?.id === media.id 
                ? "selected" 
                : "not-selected"
            }`}
            onClick={() => handleThumbnailClick(index)}
          >
            {renderThumbnail(media)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaGallery;